import { useEffect, useState } from 'react';
import clsx from 'clsx';

function initials(name?: string | null): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

interface Props {
  src?: string | null;
  name?: string | null;
  /** Tailwind size classes, e.g. "h-9 w-9". */
  sizeClass?: string;
  textClass?: string;
  bgClass?: string;
  className?: string;
}

/**
 * Avatar with graceful fallback: stale/broken image URLs (e.g. expired S3
 * presigns mirrored into Firestore dmParticipants) fall back to initials
 * instead of the browser's broken-image icon.
 */
export default function Avatar({
  src,
  name,
  sizeClass = 'h-9 w-9',
  textClass = 'text-xs',
  bgClass = 'bg-flock-navy-400',
  className,
}: Props) {
  const [failed, setFailed] = useState(false);

  // A new URL gets a fresh chance
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        onError={() => setFailed(true)}
        className={clsx(sizeClass, 'shrink-0 rounded-full object-cover', className)}
      />
    );
  }

  return (
    <div
      className={clsx(
        sizeClass,
        bgClass,
        textClass,
        'flex shrink-0 items-center justify-center rounded-full font-semibold text-white',
        className
      )}
    >
      {initials(name)}
    </div>
  );
}
